import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

/* -------------------------------------------------------------------------- */
/*                             TYPE DEFINITIONS                               */
/* -------------------------------------------------------------------------- */

export type TopUpFeeBreakdown = {
  amount: number;
  feePercentage: number;
  serviceFee: number;
  totalCharged: number;
  walletCredit: number;
};

/* -------------------------------------------------------------------------- */
/*                           FEE CALCULATION HELPERS                          */
/* -------------------------------------------------------------------------- */

/**
 * Dynamic service fee tiers for wallet top-ups
 *
 * Amount Range (€)        Fee %
 * ─────────────────────────────
 * 10 – 49.99              5.0%
 * 50 – 99.99              4.5%
 * 100 – 249.99            4.0%
 * 250 – 499.99            3.5%
 * 500 – 999.99            3.0%
 * 1,000 – 2,499.99        2.5%
 * 2,500 – 4,999.99        2.0%
 * 5,000 – 9,999.99        1.5%
 * 10,000 – 49,999.99      1.0%
 * 50,000 – 99,999.99      0.75%
 * 100,000 – 499,999.99    0.5%
 * 500,000 – 1,000,000     0.25%
 */
export const calculateTopUpServiceFee = (amount: number): number => {
  if (amount >= 500000) return parseFloat((amount * 0.0025).toFixed(2));
  if (amount >= 100000) return parseFloat((amount * 0.005).toFixed(2));
  if (amount >= 50000) return parseFloat((amount * 0.0075).toFixed(2));
  if (amount >= 10000) return parseFloat((amount * 0.01).toFixed(2));
  if (amount >= 5000) return parseFloat((amount * 0.015).toFixed(2));
  if (amount >= 2500) return parseFloat((amount * 0.02).toFixed(2));
  if (amount >= 1000) return parseFloat((amount * 0.025).toFixed(2));
  if (amount >= 500) return parseFloat((amount * 0.03).toFixed(2));
  if (amount >= 250) return parseFloat((amount * 0.035).toFixed(2));
  if (amount >= 100) return parseFloat((amount * 0.04).toFixed(2));
  if (amount >= 50) return parseFloat((amount * 0.045).toFixed(2));
  return parseFloat((amount * 0.05).toFixed(2));
};

export const getTopUpFeePercentage = (amount: number): number => {
  if (amount >= 500000) return 0.25;
  if (amount >= 100000) return 0.5;
  if (amount >= 50000) return 0.75;
  if (amount >= 10000) return 1.0;
  if (amount >= 5000) return 1.5;
  if (amount >= 2500) return 2.0;
  if (amount >= 1000) return 2.5;
  if (amount >= 500) return 3.0;
  if (amount >= 250) return 3.5;
  if (amount >= 100) return 4.0;
  if (amount >= 50) return 4.5;
  return 5.0;
};

/* -------------------------------------------------------------------------- */
/*                 MAIN WALLET TOP-UP FEE CALCULATION THUNK                   */
/* -------------------------------------------------------------------------- */

export const calculateTopUpFees = createAsyncThunk(
  "walletServiceFees/calculateTopUpFees",
  async ({ amount }: { amount: number }) => {
    const safeAmount = Math.max(10, Math.min(1000000, amount));

    const feePercentage = getTopUpFeePercentage(safeAmount);
    const serviceFee = calculateTopUpServiceFee(safeAmount);
    const totalCharged = parseFloat((safeAmount + serviceFee).toFixed(2));
    const walletCredit = safeAmount;

    return {
      amount: safeAmount,
      feePercentage,
      serviceFee,
      totalCharged,
      walletCredit,
    };
  }
);

/* -------------------------------------------------------------------------- */
/*                              REDUX SLICE                                   */
/* -------------------------------------------------------------------------- */

const walletServiceFeeSlice = createSlice({
  name: "walletServiceFees",
  initialState: {
    amount: 0,
    feePercentage: 0,
    serviceFee: 0,
    totalCharged: 0,
    walletCredit: 0,
    feesCalculated: false,
  },
  reducers: {
    resetTopUpFees: (state) => {
      state.amount = 0;
      state.feePercentage = 0;
      state.serviceFee = 0;
      state.totalCharged = 0;
      state.walletCredit = 0;
      state.feesCalculated = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      calculateTopUpFees.fulfilled,
      (state, action) => {
        const {
          amount,
          feePercentage,
          serviceFee,
          totalCharged,
          walletCredit,
        } = action.payload;

        state.amount = amount;
        state.feePercentage = feePercentage;
        state.serviceFee = serviceFee;
        state.totalCharged = totalCharged;
        state.walletCredit = walletCredit;
        state.feesCalculated = true;
      }
    );
  },
});

export const { resetTopUpFees } = walletServiceFeeSlice.actions;

export default walletServiceFeeSlice.reducer;